<script setup>
import { useStore } from 'vuex';
import { computed, onMounted, ref, watch } from 'vue';
import ResumeParsingLoader from '@/assets/icons/resumeParsingLoader.svg';
import CrossIcon from '@/assets/icons/cross2.svg';
import CheckIcon from '@/assets/icons/check.svg';

const store = useStore();
const batchId = computed(() => store.getters['batchId']);
const batchInfo = computed(() => store.getters['batchInfo']);
const fetchingBatchError = computed(() => store.getters['fetchingBatchError']);

const largeLoaderMode = ref(true);

onMounted(() => {
  setTimeout(() => (largeLoaderMode.value = false), 1500);
  return store.dispatch('getBatchProgress');
});

const percentage = computed(() =>
  batchInfo.value ? (batchInfo.value.importsDone / batchInfo.value.importsTotal) * 100 * 4 : 0
);
const displayedPercentage = ref(0);
const importIsFinished = computed(() => displayedPercentage.value >= 400);

watch(percentage, newValue => {
  while (newValue > displayedPercentage.value) {
    displayedPercentage.value += 1;
  }
});

const dynamicWidth = computed(() => (batchInfo.value?.importsTotal ? `${percentage.value / 4}%` : '0%'));

// Border segment styles
const borderTopWidth = computed(() => (displayedPercentage.value >= 100 ? '100%' : `${displayedPercentage.value}%`));

const borderRightHeight = computed(() => {
  if (displayedPercentage.value >= 200) return '100%';
  return displayedPercentage.value < 100 ? '0%' : `${displayedPercentage.value - 100}%`;
});

const borderBottomWidth = computed(() => {
  if (displayedPercentage.value >= 300) return '100%';
  return displayedPercentage.value < 200 ? '0%' : `${displayedPercentage.value - 200}%`;
});

const borderLeftHeight = computed(() => {
  if (displayedPercentage.value >= 400) return '100%';
  return displayedPercentage.value < 300 ? '0%' : `${displayedPercentage.value - 300}%`;
});

const retryParsing = async () => {
  await store.dispatch('resumes.retryResumesParsing');
  store.dispatch('getBatchProgress');
};
</script>

<template>
  <div
    v-if="batchId"
    class="upload-loader"
    :class="{ 'small-loader': !largeLoaderMode, achieved: importIsFinished }"
    @mouseenter="largeLoaderMode = true"
    @mouseleave="largeLoaderMode = false"
  >
    <!-- Border Progress -->
    <div v-if="!largeLoaderMode" class="border-progress">
      <div class="side top" :style="{ width: borderTopWidth }" />
      <div class="side right" :style="{ height: borderRightHeight }" />
      <div class="side bottom" :style="{ width: borderBottomWidth }" />
      <div class="side left" :style="{ height: borderLeftHeight }" />
    </div>

    <!-- Loader Content -->
    <div class="upload-infos">
      <CheckIcon v-if="importIsFinished" class="icon-achieved" />
      <CrossIcon v-else-if="fetchingBatchError" class="icon-error" />
      <ResumeParsingLoader v-else class="icon" />

      <template v-if="largeLoaderMode">
        <span v-if="importIsFinished">{{ $t('popUp.resumeParsing.filesAreDoneImporting') }}</span>
        <span v-else-if="fetchingBatchError" class="error">{{ $t('popUp.resumeParsing.dataFetchingError') }}</span>
        <span v-else>{{ $t('popUp.resumeParsing.filesAreImporting') }}</span>
      </template>
    </div>

    <span v-if="!batchInfo && !fetchingBatchError">{{ $t('popUp.resumeParsing.datasAreLoading') }}</span>
    <Transition name="upload-loader-text">
      <span v-if="batchInfo && !fetchingBatchError" class="upload-loader-text"
        >{{ batchInfo.importsDone }} sur {{ batchInfo.importsTotal }}</span
      >
      <span v-else-if="fetchingBatchError" class="upload-loader-text error"
        >{{ $t('popUp.resumeParsing.error') }}...</span
      >
    </Transition>

    <div v-show="largeLoaderMode" class="loader">
      <div v-if="fetchingBatchError" class="progress error"></div>
      <div v-else-if="!batchInfo" class="progress inactive"></div>
      <div v-else class="progress ongoing" :style="{ width: dynamicWidth }"></div>
    </div>

    <span v-if="fetchingBatchError && largeLoaderMode" class="retry-msg" @click="retryParsing">Essayer a nouveau</span>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.upload-loader {
  z-index: 10000;
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  padding: 2rem;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 7px 0 #dddddd;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.4s;
  overflow: hidden;

  &.small-loader {
    flex-direction: row;
    padding: 0.7rem 1rem;
    gap: 0.8rem;
  }

  &.achieved {
    .border-progress {
      .side {
        background-color: colors.$light-blue-color;
      }
    }
    .upload-loader-text {
      color: colors.$light-blue-color;
    }

    .upload-infos {
      color: colors.$light-blue-color;
    }
  }

  .upload-infos {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    z-index: 10;

    .error {
      color: red;
    }

    > .icon {
      animation: spin 5s linear infinite;

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    }

    > .icon-achieved {
      color: colors.$light-blue-color;
    }

    > .icon-error {
      color: red;
      width: 12px;
      height: 12px;
    }

    > span {
      font-family: Inter, Lato, Arial, sans-serif;
      font-weight: 500;
      font-size: 1rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .loader {
    z-index: 100;
    width: 400px;
    height: 1px;
    background-color: rgba(51, 51, 51, 0.5);
    position: relative;

    .progress {
      background-color: colors.$light-blue-color;
      height: 3px;
      top: -1px;
      position: absolute;

      &.inactive {
        width: 4.5rem;
        animation: 2s linear 0s infinite alternate slide;

        @keyframes slide {
          from {
            left: 0;
          }
          to {
            left: calc(100% - 4.5rem);
          }
        }
      }

      &.ongoing {
        transition: width 0.2s ease-in-out;
      }

      &.error {
        width: 100%;
        background-color: red;
      }
    }
  }

  .upload-loader-text {
    color: black;
    z-index: 100;

    &.error {
      color: red;
    }
  }

  .border-progress {
    pointer-events: none;
    position: absolute;
    inset: 0;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      right: 3px;
      bottom: 3px;
      border-radius: 5px;
      background-color: white;
      z-index: 3;
      overflow: hidden;
    }

    .side {
      position: absolute;
      background-color: black;
      transition: all 0.3s ease;
    }

    .top {
      top: 0;
      left: 0;
      height: 8px;
      width: 0;
    }

    .right {
      top: 8px;
      right: 0;
      width: 8px;
      height: 0;
    }

    .bottom {
      bottom: 0;
      right: 8px;
      height: 8px;
      width: 0;
    }

    .left {
      bottom: 8px;
      left: 0;
      width: 6px;
      height: 0;
    }
  }

  .retry-msg {
    text-decoration: underline;
    font-weight: bold;
    cursor: pointer;
    color: #333333;
    user-select: none;
  }
}

.upload-loader-text-enter-active,
.upload-loader-text-leave-active {
  transition: all 0.3s ease;
}

.upload-loader-text-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}
.upload-loader-text-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
